import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';

import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminDetailTableHeader,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminPanelTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminRowMenuIcon,
  AdminRowMenuItem,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  adminFormInputClasses,
  adminFormTextareaClasses,
  adminWizardSelectClasses,
  ADMIN_TAB_CONTENT_WIDE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  AdminFormFieldError,
} from '../shared/adminUx';
import { POPUP_FORM_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import useClickOutside from '../../../Hooks/useClickOutside';
import {
  ADMIN_OPENED_CASE_KEYS,
  openCaseAndAdvanceStatus,
} from '../../../utils/adminOpenedCases';
import {
  getTicketAuditLogRows,
  recordTicketActivity,
  syncLocalAuditLogsToChangeHistory,
} from '../../../data/changeHistoryAudit';

const REOPENING_DAYS = 7;

const addCalendarDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const nextResolutionRecordId = (resolutions = []) => {
  const maxNum = resolutions.reduce((max, r) => {
    const match = String(r.resolutionRecordId || '').match(/(\d+)/);
    const n = match ? parseInt(match[1], 10) : 0;
    return Math.max(max, n);
  }, 70);
  return `RES-${String(maxNum + 1).padStart(6, '0')}`;
};

/** Normalize legacy aliases to Support Ticket statuses. */
const normalizeTicketStatus = (status) => {
  if (status === 'Submitted') return 'New';
  if (status === 'Decided') return 'Resolved';
  return status || 'New';
};

const isTicketPermanentlyReadOnly = (status) => {
  const s = normalizeTicketStatus(status);
  return s === 'Closed' || s === 'Withdrawn';
};

/** No support replies once the ticket is finished (Resolved / Closed / Withdrawn). */
const isTicketReplyClosed = (status) => {
  const s = normalizeTicketStatus(status);
  return s === 'Resolved' || s === 'Closed' || s === 'Withdrawn';
};

const isWithinReopeningPeriod = (ticket) => {
  if (!ticket || normalizeTicketStatus(ticket.status) !== 'Resolved') return false;
  const latest = (ticket.resolutions || [])[0];
  if (!latest?.reopeningUntil) return false;
  return new Date(latest.reopeningUntil).getTime() > Date.now();
};

const nextFraudCaseId = () => {
  const n = 1000 + Math.floor(Math.random() * 9000);
  return `FS-${String(n).padStart(6, '0')}`;
};

/** Header / list — show staff name only; Unassigned when empty. */
const formatAssignedDisplay = (raw) => {
  if (!raw || String(raw).toLowerCase() === 'unassigned') return 'Unassigned';
  const name = String(raw)
    .replace(/^Fraud\s*&\s*Security — /i, '')
    .replace(/^(Admin|Compliance|Support|Moderator|Owner|Finance|Development) — /i, '')
    .replace(/^Admin: /, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .split(' (')[0]
    .trim();
  return name || 'Unassigned';
};

const resolveRelatedRecordPath = (recordType, recordId) => {
  if (!recordId || recordId === '—') return null;
  const type = String(recordType || '').toLowerCase();
  if (type.includes('compliance')) {
    return `/admin/verification-compliance/${encodeURIComponent(recordId)}`;
  }
  if (type.includes('invoice') || type.includes('transaction')) {
    return `/admin/finance/transactions/${recordId}`;
  }
  if (type.includes('post')) return `/admin/posts/${recordId}`;
  if (type.includes('review')) return `/admin/moderation/queue/${recordId}`;
  if (type.includes('customer')) return `/admin/users/customers/${recordId}`;
  if (type.includes('business')) return `/admin/users/businesses/${recordId}`;
  return null;
};

const isComplianceAskMoreInfoTicket = (t) =>
  Boolean(
    t?.isComplianceAskMoreInfo ||
      t?.source === 'Compliance — Ask More Info' ||
      t?.channel === 'Compliance — Ask More Info' ||
      String(t?.relatedRecordType || '')
        .toLowerCase()
        .includes('compliance')
  );

/** Active while Compliance awaits requested info inside the recorded response window. */
const hasActiveComplianceResponseProtection = (t) => {
  if (!isComplianceAskMoreInfoTicket(t)) return false;
  if (t?.complianceResponseReceived) return false;
  if (t?.complianceResponseProtection === false) return false;
  if (t?.complianceResponseProtection === true) return true;
  if (!t?.complianceDueDate) return false;
  return new Date(t.complianceDueDate).getTime() > Date.now();
};

const makeAuditLogEntry = ({ timestamp, event, details, relatedRecord = '—' }) => ({
  dateTime: timestamp instanceof Date ? timestamp : new Date(timestamp || Date.now()),
  event,
  details,
  relatedRecord: relatedRecord || '—',
});

const mapTicketCentralAudit = (rows) =>
  (rows || []).map((row) => ({
    dateTime: row.timestamp ? new Date(row.timestamp) : row.dateTime,
    event: row.event,
    details: row.details,
    relatedRecord: row.relatedRecord || '—',
    changeId: row.changeId,
  }));

const recordAndRefreshTicketAudit = (ticketRef, entry, previousLogs = []) => {
  if (!ticketRef) return [entry, ...(previousLogs || [])];
  syncLocalAuditLogsToChangeHistory({
    logs: previousLogs,
    module: 'Support Tickets',
    sourceModuleRef: 'support_tickets',
    sourceItemRef: ticketRef,
    ticketRef,
    changedItem: ticketRef,
    itemId: ticketRef,
  });
  const iso =
    entry.dateTime instanceof Date
      ? entry.dateTime.toISOString()
      : new Date(entry.dateTime || Date.now()).toISOString();
  recordTicketActivity({
    ticketRef,
    changeType: entry.event,
    activityDetails: entry.details,
    relatedRecord: entry.relatedRecord,
    sourceChangeRef: `support_tickets::${ticketRef}::${entry.event}::${iso}`,
  });
  return mapTicketCentralAudit(getTicketAuditLogRows(ticketRef));
};

const refreshTicketAuditFromStore = (ticketRef, fallbackLogs = []) => {
  if (!ticketRef) return fallbackLogs;
  syncLocalAuditLogsToChangeHistory({
    logs: fallbackLogs,
    module: 'Support Tickets',
    sourceModuleRef: 'support_tickets',
    sourceItemRef: ticketRef,
    ticketRef,
    changedItem: ticketRef,
    itemId: ticketRef,
  });
  return mapTicketCentralAudit(getTicketAuditLogRows(ticketRef));
};

/** Full entry point label (no dashboard shortening). */
const formatEntryPointDisplay = (ticketOrSource) => {
  const raw =
    typeof ticketOrSource === 'object' && ticketOrSource != null
      ? ticketOrSource.source || ticketOrSource.channel
      : ticketOrSource;
  if (!raw) return '—';
  return String(raw).trim() || '—';
};

const SAMPLE_PDF_PREVIEW_URL =
  'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';

function getAttachmentPreviewKind(att, resolvedUrl) {
  const name = (att?.name || '').toLowerCase();
  const type = (att?.type || '').toLowerCase();
  const u = (resolvedUrl || '').toLowerCase();
  if (name.endsWith('.pdf') || type === 'pdf' || /\.pdf(\?|$)/i.test(u)) return 'pdf';
  if (
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name) ||
    ['png', 'jpeg', 'jpg', 'gif', 'webp'].some((x) => type === x || type.includes('image')) ||
    /picsum\.photos|\.(png|jpe?g|gif|webp)(\?|$)/i.test(u)
  ) {
    return 'image';
  }
  return 'other';
}

function resolveAttachmentDisplayUrl(att) {
  const raw = att?.url;
  if (raw && raw !== '#' && /^https?:\/\//i.test(String(raw).trim())) {
    return String(raw).trim();
  }
  const kindByName = getAttachmentPreviewKind(att, '');
  if (kindByName === 'pdf') return SAMPLE_PDF_PREVIEW_URL;
  if (kindByName === 'image') {
    const seed = encodeURIComponent((att?.name || 'file').replace(/[^a-z0-9]/gi, '').slice(0, 40) || 'file');
    return `https://picsum.photos/seed/tp-${seed}/1200/800`;
  }
  return null;
}

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketFromState = location.state?.ticket;

  const [activeTab, setActiveTab] = useState('overview');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  // Reply form state
  const [replyText, setReplyText] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [replyError, setReplyError] = useState('');
  const [replyAttachmentError, setReplyAttachmentError] = useState('');
  const replyComposerRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const composerMenuRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [composerMenuOpen, setComposerMenuOpen] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [showResolveForm, setShowResolveForm] = useState(false);

  useClickOutside(composerMenuRef, () => setComposerMenuOpen(false));
  const [createFsModalOpen, setCreateFsModalOpen] = useState(false);
  const [fsAffectedAccount, setFsAffectedAccount] = useState('');
  const [fsConcernSummary, setFsConcernSummary] = useState('');
  const [fsEvidence, setFsEvidence] = useState('');
  const [fsPriority, setFsPriority] = useState('Medium');
  const [fsFormError, setFsFormError] = useState('');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [resolutionSummaryError, setResolutionSummaryError] = useState('');
  const [actionError, setActionError] = useState('');

  const openAttachmentPreview = (att) => {
    const url = resolveAttachmentDisplayUrl(att);
    const kind = url ? getAttachmentPreviewKind(att, url) : getAttachmentPreviewKind(att, '');
    setAttachmentPreview({ attachment: att, url, kind });
  };

  const closeAttachmentPreview = () => setAttachmentPreview(null);

  useEffect(() => {
    if (!attachmentPreview) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeAttachmentPreview();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [attachmentPreview]);

  const SUPPORT_REPLY_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
  const SUPPORT_REPLY_ALLOWED_EXTENSIONS = [
    '.pdf',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.doc',
    '.docx',
    '.txt',
  ];

  const getTicketSubmitterName = (t) =>
    t?.submittedByName ||
    t?.customerName ||
    t?.businessName ||
    t?.customer?.name ||
    t?.business?.name ||
    'Submitter';

  const getTicketSubmitterType = (t) => {
    if (t?.submittedAs === '—') return '—';
    return (
      t?.submittedAs ||
      t?.accountType ||
      t?.type ||
      (t?.businessId || t?.business ? 'Business' : t?.customerId || t?.customer ? 'Customer' : 'Visitor')
    );
  };

  /** Support Messages: original request first, then chronological replies (oldest → newest). */
  const supportMessagesThread = useMemo(() => {
    if (!ticket) return [];
    const submitterName = getTicketSubmitterName(ticket);
    const submitterType = getTicketSubmitterType(ticket);
    const bubbles = [];

    const complianceOriginated = isComplianceAskMoreInfoTicket(ticket);

    if (ticket.description) {
      bubbles.push({
        id: 'original-request',
        senderName: complianceOriginated
          ? formatAssignedDisplay(ticket.submittedByName || ticket.assignee) !== 'Unassigned'
            ? formatAssignedDisplay(ticket.submittedByName || ticket.assignee)
            : 'Support'
          : submitterName,
        senderType: complianceOriginated ? 'Support Tickets Team' : submitterType,
        isSupport: complianceOriginated,
        message: ticket.description,
        attachments: ticket.attachments || [],
        timestamp: ticket.submittedOn || ticket.createdDate,
      });
    }

    const sorted = [...(ticket.messages || [])].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });

    let skippedLegacyOriginal = false;

    sorted.forEach((m, idx) => {
      const isSupport =
        m.sender === 'Support' ||
        m.sender === 'Support Team' ||
        m.senderType === 'Support Tickets Team';

      if (ticket.description && !isSupport) {
        if (m.message && m.message === ticket.description) return;
        // Skip legacy mock row that duplicates the original request (often id msg1 / first row)
        if (!skippedLegacyOriginal && (m.id === 'msg1' || idx === 0)) {
          skippedLegacyOriginal = true;
          return;
        }
      }

      let senderName;
      let senderType;
      if (isSupport) {
        senderName = m.senderName || formatAssignedDisplay(ticket.assignee);
        if (senderName === 'Unassigned') senderName = 'Support';
        senderType = 'Support Tickets Team';
      } else {
        senderName = m.senderName || submitterName;
        const rawType = m.senderType || m.sender || submitterType;
        senderType =
          rawType === 'Customer' || rawType === 'Business' || rawType === 'Visitor'
            ? rawType
            : submitterType;
      }

      bubbles.push({
        id: m.id || `msg-${idx}`,
        senderName,
        senderType,
        isSupport,
        message: m.message,
        attachments: m.attachments || [],
        timestamp: m.timestamp || null,
      });
    });

    return bubbles;
  }, [ticket]);

  useEffect(() => {
    if (activeTab !== 'messages') return undefined;
    const timer = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    }, 200);
    return () => clearTimeout(timer);
  }, [activeTab, supportMessagesThread.length]);

  // Format date
  const formatDate = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format date and time
  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) + 
           ', ' + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  // Audit log datetime: "05 Apr 2026, 9:14 AM"
  const formatAuditDateTime = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    const datePart = d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  };

  // Generate dummy ticket data
  const generateDummyTicket = () => {
    const now = new Date();
    const resolvedTicketId = ticketId || ticketFromState?.ticketId || 'ST-000183';

    // Use ticket from state if available, otherwise generate
    if (ticketFromState) {
      if (isComplianceAskMoreInfoTicket(ticketFromState)) {
        const businessId = ticketFromState.businessId || ticketFromState.business?.id || 'BUS-001042';
        const caseId = ticketFromState.relatedRecordId || 'CMP-000218';
        const assigneeName =
          formatAssignedDisplay(ticketFromState.assignee) !== 'Unassigned'
            ? formatAssignedDisplay(ticketFromState.assignee)
            : 'Sana Malik';
        const submittedOn = ticketFromState.submittedOn || ticketFromState.createdDate || now;
        const complianceDueDate =
          ticketFromState.complianceDueDate || new Date('2026-08-22T10:00:00');
        const complianceResponsePeriodDays = ticketFromState.complianceResponsePeriodDays ?? 14;
        const infoDescription =
          ticketFromState.description ||
          'Please upload a clear copy of your current Plumbing Licence certificate, including expiry date and issuing authority, so we can complete your Compliance Case review.';

        return {
          ...ticketFromState,
          ticketId: ticketFromState.ticketId || resolvedTicketId,
          submittedAs: '—',
          ticketType: 'General Enquiry',
          category: 'General Enquiry',
          source: 'Compliance — Ask More Info',
          channel: 'Compliance — Ask More Info',
          isComplianceAskMoreInfo: true,
          status: ticketFromState.status || 'Awaiting Reply',
          submittedByName: ticketFromState.submittedByName || assigneeName,
          description: infoDescription,
          businessId,
          businessName: ticketFromState.businessName || 'Apex Plumbing',
          relatedRecordType: 'Compliance Case',
          relatedRecordId: caseId,
          complianceResponsePeriodDays,
          complianceDueDate,
          complianceResponseProtection: ticketFromState.complianceResponseProtection !== false,
          submittedOn,
          createdDate: submittedOn,
          waitingOn: '—',
          email: ticketFromState.email || 'compliance@apexplumbing.example.com',
          phone: '—',
          accountId: null,
          type: 'Business',
          accountType: '—',
          business: {
            name: ticketFromState.businessName || 'Apex Plumbing',
            id: businessId,
            email: ticketFromState.email || 'compliance@apexplumbing.example.com',
            phone: '—',
            accountStatus: 'Active',
            memberSince: ticketFromState.business?.memberSince ?? submittedOn,
            lastActive: ticketFromState.lastUpdated ?? submittedOn,
            profileId: businessId,
          },
          customer: null,
          attachments: ticketFromState.attachments || [],
          messages: ticketFromState.messages || [],
          resolutions: ticketFromState.resolutions || [],
          moderationCases: ticketFromState.moderationCases || [],
          auditLogs:
            ticketFromState.auditLogs?.length > 0
              ? ticketFromState.auditLogs
              : [
                  makeAuditLogEntry({
                    timestamp: submittedOn,
                    event: 'Ticket created',
                    details: 'Compliance Ask More Info ticket opened from Compliance Case',
                    relatedRecord: caseId,
                  }),
                  makeAuditLogEntry({
                    timestamp: submittedOn,
                    event: 'Support message sent',
                    details: 'Information request sent to business recipient',
                    relatedRecord: caseId,
                  }),
                ],
        };
      }

      // Build business or customer object based on ticket type
      let businessObj = null;
      let customerObj = null;
      
      if (ticketFromState.type === 'Business' || ticketFromState.submittedAs === 'Business' || ticketFromState.accountType === 'Business') {
        const resolvedBusinessId = ticketFromState.businessId || ticketFromState.accountId || ticketFromState.business?.id || 'BUS-2000';
        businessObj = {
          name: ticketFromState.businessName || ticketFromState.submittedByName || 'Business Name',
          id: resolvedBusinessId,
          email: ticketFromState.business?.email || ticketFromState.email || 'support@business.example.com',
          phone: ticketFromState.business?.phone || ticketFromState.phone || '04XX XXX XXX',
          category: 'Business Category',
          subscription: 'Pro Annual',
          verification: 'Verified',
          accountStatus: ticketFromState.business?.accountStatus || 'Active',
          memberSince: ticketFromState.business?.memberSince ?? ticketFromState.createdDate ?? new Date('2024-01-15'),
          lastActive: ticketFromState.business?.lastActive ?? ticketFromState.lastUpdated ?? new Date(),
          profileId: ticketFromState.business?.profileId || resolvedBusinessId
        };
      } else if (ticketFromState.type === 'Customer' || ticketFromState.submittedAs === 'Customer' || ticketFromState.accountType === 'Customer') {
        const resolvedCustomerId = ticketFromState.customerId || ticketFromState.accountId || ticketFromState.customer?.id || 'CUS-1000';
        customerObj = {
          name: ticketFromState.customerName || ticketFromState.submittedByName || 'Customer Name',
          id: resolvedCustomerId,
          email: ticketFromState.customer?.email || ticketFromState.email || 'customer@email.com',
          phone: ticketFromState.customer?.phone || ticketFromState.phone || '04XX XXX XXX',
          accountStatus: ticketFromState.customer?.accountStatus || 'Active',
          memberSince: ticketFromState.customer?.memberSince ?? ticketFromState.createdDate ?? new Date('2025-08-02'),
          lastActive: ticketFromState.customer?.lastActive ?? ticketFromState.lastUpdated ?? new Date()
        };
      }

      const queueFallbackDescription = (() => {
        const sub = (ticketFromState.subject || 'this request').trim();
        return (
          `We're contacting you regarding ${sub}. We ran into this while using the platform during normal activity and need clarity on next steps, typical response times, and anything else you need from us ` +
          '(for example screenshots, account identifiers, or browser details). We have notes on what we already tried. Please confirm that this ticket was received and let us know the best way to follow up — we can verify identity or arrange a quick call if that helps resolve things faster. Thank you for your help.'
        );
      })();

      const submittedAs =
        ticketFromState.submittedAs ||
        ticketFromState.accountType ||
        ticketFromState.type ||
        (businessObj ? 'Business' : customerObj ? 'Customer' : 'Visitor');
      const ticketType = ticketFromState.ticketType || ticketFromState.category || 'General Enquiry';
      const source =
        ticketFromState.source ||
        (ticketFromState.channel === 'Help Centre' || ticketFromState.channel === 'Website Form'
          ? 'Help Centre'
          : ticketFromState.channel === 'Business' || ticketFromState.channel === 'Business Dashboard'
            ? 'Business Dashboard'
            : ticketFromState.channel === 'Customer' || ticketFromState.channel === 'Customer Dashboard'
              ? 'Customer Dashboard'
              : ticketFromState.channel || 'Help Centre');
      const relatedRecordType =
        ticketFromState.relatedRecordType ??
        (String(ticketType).includes('Billing') ? 'Invoice' : null);
      const relatedRecordId =
        ticketFromState.relatedRecordId ??
        (relatedRecordType === 'Invoice' ? 'INV-002461' : null);

      const status = normalizeTicketStatus(ticketFromState.status);
      const assigneeName = formatAssignedDisplay(ticketFromState.assignee);
      const resolvedOnSample =
        ticketFromState.lastUpdated || ticketFromState.submittedOn || ticketFromState.createdDate || now;
      const defaultResolutions =
        status === 'Resolved' || status === 'Closed' || status === 'Withdrawn'
          ? [
              {
                resolutionRecordId: 'RES-000071',
                resolutionSummary:
                  'The invoice was confirmed as correct for the current monthly Pro billing period.',
                resolvedBy: assigneeName !== 'Unassigned' ? assigneeName : 'Sana Malik',
                resolvedOn: new Date(resolvedOnSample),
                reopeningUntil: addCalendarDays(resolvedOnSample, REOPENING_DAYS),
              },
            ]
          : [];
      return {
        ...ticketFromState,
        status,
        submittedAs,
        ticketType,
        source,
        submittedOn: ticketFromState.submittedOn || ticketFromState.createdDate || now,
        relatedRecordType,
        relatedRecordId,
        resolutions: ticketFromState.resolutions || defaultResolutions,
        description: ticketFromState.description || queueFallbackDescription,
        attachments: ticketFromState.attachments || [
          { id: 'att1', name: 'invoice_4481.pdf', type: 'PDF', url: '#' },
          { id: 'att2', name: 'stripe_charge.png', type: 'Image', url: '#' }
        ],
        messages: ticketFromState.messages || [
          {
            id: 'msg1',
            sender: ticketFromState.type === 'Business' ? 'Business' : 'Customer',
            senderName: ticketFromState.businessName || ticketFromState.customerName || 'User',
            channel: ticketFromState.channel || 'Web form',
            time: '09:31',
            timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000),
            message: ticketFromState.type === 'Customer'
              ? "I'm unable to reset my password. The reset link keeps expiring — I've tried requesting a new one three times now but each link says it's invalid or has expired before I can use it. I'm using the correct email address and checking my inbox within minutes of receiving the email. Could you please look into this or send a manual reset? I've been locked out of my account for two days.\n\nI've attached a screenshot of the error I'm seeing when I click the reset link. It says \"This link has expired\" even though I received the email less than an hour ago. I'm on Chrome (Windows 11), cleared cache and cookies, and tried both normal and incognito windows. I've also confirmed there are no VPN or corporate firewall issues on my side. Please let me know if there's another way to regain access or if you need logs from my browser.\n\nBusiness impact: I can't respond to quote requests or messages until I'm back in — happy to verify identity another way if needed."
              : "We were charged twice today for our Pro Annual subscription. The first charge went through at 8:42 AM and the second at 8:44 AM — we only intended to renew once. Could you please investigate and process a refund for the duplicate amount? We've attached our invoice and the payment confirmation from our bank.\n\nInvoice reference #4481. Both line items show the same plan and the same day; our bank feed shows two separate Tradesposter debits. Our billing contact email matches this ticket. We need this resolved before month-end — please confirm expected timelines, whether we should disable auto-renew temporarily, and if a credit can be applied to the next cycle. Happy to provide any other documentation or join a call with finance.\n\nThanks for your help — we rely on the platform for daily job leads and want to make sure billing is correct going forward.",
            attachments: ticketFromState.type === 'Customer'
              ? [{ id: 'att1', name: 'password-error.png', type: 'PNG', size: '312 KB' }]
              : [
                  { id: 'att1', name: 'invoice_4481.pdf', type: 'PDF', size: '245 KB' },
                  { id: 'att2', name: 'payment_confirmation.png', type: 'PNG', size: '189 KB' },
                ],
          },
        ],
        moderationCases: ticketFromState.moderationCases || [],
        auditLogs:
          ticketFromState.auditLogs?.length > 0
            ? ticketFromState.auditLogs
            : [
                makeAuditLogEntry({
                  timestamp: ticketFromState.submittedOn || ticketFromState.createdDate || now,
                  event: 'Ticket created',
                  details: `Ticket submitted via ${formatEntryPointDisplay(ticketFromState)}`,
                  relatedRecord: ticketFromState.ticketId || resolvedTicketId,
                }),
                makeAuditLogEntry({
                  timestamp: new Date((ticketFromState.submittedOn || ticketFromState.createdDate || now).getTime() + 5 * 60 * 1000),
                  event: 'Ticket assigned',
                  details: `Assigned to ${formatAssignedDisplay(ticketFromState.assignee)}`,
                  relatedRecord: ticketFromState.ticketId || resolvedTicketId,
                }),
              ],
        business: businessObj || ticketFromState.business,
        customer: customerObj || ticketFromState.customer,
        accountType: submittedAs,
        type: submittedAs,
        waitingOn: ticketFromState.waitingOn || '—',
        accountId:
          submittedAs === 'Visitor'
            ? null
            : ticketFromState.accountId ||
              ticketFromState.customerId ||
              ticketFromState.businessId ||
              ticketFromState.customer?.id ||
              ticketFromState.business?.id ||
              null,
        email: ticketFromState.email || customerObj?.email || businessObj?.email || ticketFromState.customer?.email || ticketFromState.business?.email || null,
        phone: ticketFromState.phone || customerObj?.phone || businessObj?.phone || ticketFromState.customer?.phone || ticketFromState.business?.phone || null,
        submittedByName:
          ticketFromState.submittedByName ||
          ticketFromState.customerName ||
          ticketFromState.businessName ||
          customerObj?.name ||
          businessObj?.name ||
          null,
        moderationCaseId: ticketFromState.moderationCaseId ?? null,
        complianceCaseId: ticketFromState.complianceCaseId ?? null,
        fraudCaseId: ticketFromState.fraudCaseId ?? null,
        postId: ticketFromState.postId ?? null,
        reviewId: ticketFromState.reviewId ?? null,
        transactionId: ticketFromState.transactionId ?? null
      };
    }
    
    const submittedOn = new Date('2026-08-08T13:48:00');
    const resolvedOn = new Date('2026-08-08T15:10:00');
    const sampleTicketId = resolvedTicketId === 'ST-000183' ? 'ST-000183' : resolvedTicketId;

    // Generate default ticket (spec sample ST-000183)
    return {
      ticketId: sampleTicketId,
      type: 'Business',
      accountType: 'Business',
      submittedAs: 'Business',
      accountId: 'BUS-002114',
      subject: 'Subscription invoice question',
      priority: 'Medium',
      category: 'Billing & Subscriptions',
      ticketType: 'Billing & Subscriptions',
      status: 'Resolved',
      waitingOn: '—',
      source: 'Business Dashboard',
      channel: 'Business Dashboard',
      createdDate: submittedOn,
      submittedOn,
      lastUpdated: resolvedOn,
      createdBy: 'Westside Plumbing',
      assignee: 'Sana Malik',
      businessId: 'BUS-002114',
      businessName: 'Westside Plumbing',
      submittedByName: 'Westside Plumbing',
      email: 'accounts@westsideplumbing.example.com',
      phone: '04XX XXX XXX',
      relatedRecordType: 'Invoice',
      relatedRecordId: 'INV-002461',
      resolutions: [
        {
          resolutionRecordId: 'RES-000071',
          resolutionSummary:
            'The invoice was confirmed as correct for the current monthly Pro billing period.',
          resolvedBy: 'Sana Malik',
          resolvedOn,
          reopeningUntil: new Date('2026-08-15T15:10:00'),
        },
      ],
      moderationCaseId: null,
      complianceCaseId: null,
      fraudCaseId: null,
      postId: null,
      reviewId: null,
      transactionId: 'INV-002461',
      description:
        'Can you confirm which billing period this invoice covers? We received INV-002461 for our Pro subscription but the line items and PDF header show different month labels. Our finance team needs to match this to our August accruals before month-end — please clarify whether this invoice is for the current monthly Pro period or a prior cycle, and whether any proration applies.',
      attachments: [
        { id: 'att1', name: 'invoice_4481.pdf', type: 'PDF', url: '#' },
        { id: 'att2', name: 'stripe_charge.png', type: 'PNG', url: '#' },
      ],
      messages: [
        {
          id: 'msg-reply-1',
          sender: 'Support',
          senderName: 'Sana Malik',
          senderType: 'Support Tickets Team',
          channel: 'Admin Panel',
          timestamp: new Date('2026-08-08T14:20:00'),
          message:
            'Thanks for reaching out. I have pulled up INV-002461 against your Pro billing profile and confirmed it applies to the current monthly billing period. I have attached a short summary for your finance records — please reply if anything still looks inconsistent.',
        },
      ],
      moderationCases: [],
      auditLogs: [
        makeAuditLogEntry({
          timestamp: submittedOn,
          event: 'Ticket created',
          details: 'Ticket submitted via Business Dashboard',
          relatedRecord: sampleTicketId,
        }),
        makeAuditLogEntry({
          timestamp: new Date('2026-08-08T13:52:00'),
          event: 'Ticket assigned',
          details: 'Assigned to Sana Malik',
          relatedRecord: sampleTicketId,
        }),
        makeAuditLogEntry({
          timestamp: new Date('2026-08-08T14:00:00'),
          event: 'Status changed',
          details: 'New → In Progress',
          relatedRecord: sampleTicketId,
        }),
        makeAuditLogEntry({
          timestamp: new Date('2026-08-08T14:20:00'),
          event: 'Support message sent',
          details: 'Reply sent to business',
          relatedRecord: sampleTicketId,
        }),
        makeAuditLogEntry({
          timestamp: new Date('2026-08-08T14:35:00'),
          event: 'Internal note added',
          details: 'Invoice record checked against the current Pro billing cycle.',
          relatedRecord: sampleTicketId,
        }),
        makeAuditLogEntry({
          timestamp: resolvedOn,
          event: 'Ticket resolved',
          details: 'The invoice was confirmed as correct for the current monthly Pro billing period.',
          relatedRecord: 'RES-000071',
        }),
      ],
      business: {
        name: 'Westside Plumbing',
        id: 'BUS-002114',
        email: 'accounts@westsideplumbing.example.com',
        phone: '04XX XXX XXX',
        category: 'Plumbing',
        subscription: 'Pro',
        verification: 'Verified',
        accountStatus: 'Active',
        memberSince: new Date('2024-03-10'),
        lastActive: resolvedOn,
        profileId: 'BUS-002114',
      },
      customer: null,
    };
  };

  // Fetch ticket data — opening advances New → In Progress (persisted for list return)
  useEffect(() => {
    const fetchTicket = async () => {
      setLoading(true);
      try {
        if (!ticketFromState) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        const enhancedTicket = generateDummyTicket();
        const opened = openCaseAndAdvanceStatus(
          ADMIN_OPENED_CASE_KEYS.supportTickets,
          enhancedTicket,
          'ticketId'
        );
        const ticketRef = opened?.ticketId || ticketId;
        setTicket({
          ...opened,
          auditLogs: refreshTicketAuditFromStore(ticketRef, opened?.auditLogs || []),
        });
      } catch (err) {
        console.error('Failed to load ticket:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketId, ticketFromState]);

  // Handle reply send — append to ticket.messages so it appears in Support Messages
  const handleSendReply = () => {
    if (isTicketReplyClosed(ticket?.status)) return;

    const text = replyText.trim();
    let hasError = false;

    if (!text) {
      setReplyError('Message is required.');
      hasError = true;
    } else {
      setReplyError('');
    }

    if (replyAttachmentError) {
      hasError = true;
    }

    if (hasError) return;

    const now = new Date();
    let attachments;
    if (replyFile) {
      attachments = [
        {
          id: `att-${now.getTime()}`,
          name: replyFile.name,
          type:
            (replyFile.type && replyFile.type.split('/').pop()?.toUpperCase()) ||
            'File',
          url: URL.createObjectURL(replyFile),
        },
      ];
    }

    const assigneeName = formatAssignedDisplay(ticket.assignee);
    const newMessage = {
      id: `msg-${now.getTime()}`,
      sender: 'Support',
      senderName: assigneeName !== 'Unassigned' ? assigneeName : 'Support',
      senderType: 'Support Tickets Team',
      channel: ticket.channel || 'Web',
      time: now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
      timestamp: now,
      message: text,
      ...(attachments ? { attachments } : {}),
    };

    const previousStatus = normalizeTicketStatus(ticket.status);
    // Staff reply means the ticket is no longer Awaiting Reply (no separate Mark Awaiting Reply).
    const nextStatus =
      previousStatus === 'Awaiting Reply' ? 'In Progress' : previousStatus;

    let logs = ticket.auditLogs || [];
    logs = recordAndRefreshTicketAudit(
      ticket.ticketId,
      makeAuditLogEntry({
        timestamp: now,
        event: 'Support message sent',
        details: 'Support reply recorded',
        relatedRecord: ticket.ticketId || '—',
      }),
      logs
    );
    if (nextStatus !== previousStatus) {
      logs = recordAndRefreshTicketAudit(
        ticket.ticketId,
        makeAuditLogEntry({
          timestamp: now,
          event: 'Status changed',
          details: `${previousStatus} → ${nextStatus}`,
          relatedRecord: ticket.ticketId || '—',
        }),
        logs
      );
    }

    setTicket((prev) => ({
      ...prev,
      status: nextStatus,
      messages: [...(prev.messages || []), newMessage],
      lastUpdated: now,
      auditLogs: logs,
    }));

    setReplyText('');
    setReplyFile(null);
    setReplyError('');
    setReplyAttachmentError('');
    if (replyFileInputRef.current) replyFileInputRef.current.value = '';
  };

  const handleReplyTextChange = (e) => {
    const value = e.target.value;
    setReplyText(value);
    if (replyError && value.trim()) setReplyError('');
  };

  const handleReplyFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setReplyFile(null);
      setReplyAttachmentError('');
      return;
    }

    const name = file.name.toLowerCase();
    const hasAllowedExt = SUPPORT_REPLY_ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
    if (!hasAllowedExt) {
      setReplyFile(null);
      setReplyAttachmentError('This file type is not allowed.');
      e.target.value = '';
      return;
    }

    if (file.size > SUPPORT_REPLY_MAX_ATTACHMENT_BYTES) {
      setReplyFile(null);
      setReplyAttachmentError('Attachment must be 10 MB or smaller.');
      e.target.value = '';
      return;
    }

    setReplyFile(file);
    setReplyAttachmentError('');
  };

  const handleChangeStatus = (newStatus) => {
    if (isTicketPermanentlyReadOnly(ticket?.status)) return;

    const previousStatus = ticket?.status;
    const now = new Date();
    const logEntry = makeAuditLogEntry({
      timestamp: now,
      event: 'Status changed',
      details: `${previousStatus} → ${newStatus}`,
      relatedRecord: ticket?.ticketId || '—',
    });

    setActionError('');
    setTicket((prev) => ({
      ...prev,
      status: newStatus,
      waitingOn: '—',
      lastUpdated: now,
      auditLogs: recordAndRefreshTicketAudit(prev.ticketId, logEntry, prev.auditLogs),
    }));
  };

  const handleMarkResolved = () => {
    if (isTicketPermanentlyReadOnly(ticket?.status)) return;

    if (hasActiveComplianceResponseProtection(ticket)) {
      setActionError(
        'Waiting for the Compliance response. This ticket cannot be resolved yet.'
      );
      return;
    }

    const summary = resolutionSummary.trim();
    if (!summary) {
      setResolutionSummaryError('Resolution Summary is required.');
      return;
    }
    setResolutionSummaryError('');
    setActionError('');

    const now = new Date();
    const resolvedBy =
      formatAssignedDisplay(ticket.assignee) !== 'Unassigned'
        ? formatAssignedDisplay(ticket.assignee)
        : 'Support';
    const newResolution = {
      resolutionRecordId: nextResolutionRecordId(ticket.resolutions),
      resolutionSummary: summary,
      resolvedBy,
      resolvedOn: now,
      reopeningUntil: addCalendarDays(now, REOPENING_DAYS),
    };

    setTicket((prev) => {
      let logs = prev.auditLogs || [];
      logs = recordAndRefreshTicketAudit(
        prev.ticketId,
        makeAuditLogEntry({
          timestamp: now,
          event: 'Ticket resolved',
          details: 'Resolution recorded',
          relatedRecord: newResolution.resolutionRecordId,
        }),
        logs
      );
      logs = recordAndRefreshTicketAudit(
        prev.ticketId,
        makeAuditLogEntry({
          timestamp: now,
          event: 'Status changed',
          details: `${prev.status} → Resolved`,
          relatedRecord: prev.ticketId || '—',
        }),
        logs
      );
      return {
        ...prev,
        status: 'Resolved',
        waitingOn: '—',
        lastUpdated: now,
        resolutions: [newResolution, ...(prev.resolutions || [])],
        auditLogs: logs,
      };
    });
    setResolutionSummary('');
    setShowResolveForm(false);
  };

  const openCreateFsModal = () => {
    setFsAffectedAccount(
      ticket?.businessId ||
        ticket?.business?.id ||
        ticket?.customerId ||
        ticket?.customer?.id ||
        ticket?.accountId ||
        ''
    );
    setFsConcernSummary('');
    setFsEvidence('');
    setFsPriority('Medium');
    setFsFormError('');
    setCreateFsModalOpen(true);
  };

  const closeCreateFsModal = () => {
    setCreateFsModalOpen(false);
    setFsFormError('');
  };

  const handleSaveCreateFsCase = () => {
    if (isTicketPermanentlyReadOnly(ticket?.status)) return;

    const account = fsAffectedAccount.trim();
    const concern = fsConcernSummary.trim();
    if (!account) {
      setFsFormError('Affected account is required.');
      return;
    }
    if (!concern) {
      setFsFormError('Concern summary is required.');
      return;
    }
    if (fsPriority !== 'Medium' && fsPriority !== 'High') {
      setFsFormError('F&S Priority must be Medium or High.');
      return;
    }
    setFsFormError('');

    const now = new Date();
    const caseId = nextFraudCaseId();
    const createdBy =
      formatAssignedDisplay(ticket.assignee) !== 'Unassigned'
        ? formatAssignedDisplay(ticket.assignee)
        : 'Support';

    setTicket((prev) => ({
      ...prev,
      fraudCaseId: caseId,
      lastUpdated: now,
      auditLogs: recordAndRefreshTicketAudit(
        prev.ticketId,
        makeAuditLogEntry({
          timestamp: now,
          event: 'F&S case created',
          details: `${createdBy} created ${caseId} for ${account} (${fsPriority})`,
          relatedRecord: caseId,
        }),
        prev.auditLogs
      ),
    }));
    closeCreateFsModal();
  };

  const canCreateFsCase = !isTicketPermanentlyReadOnly(ticket?.status);
  const complianceTicket = isComplianceAskMoreInfoTicket(ticket);
  const complianceProtectionActive =
    hasActiveComplianceResponseProtection(ticket) &&
    (ticket?.status === 'In Progress' || ticket?.status === 'Awaiting Reply');

  const overviewSubmittedAs =
    ticket?.submittedAs === '—'
      ? '—'
      : ticket?.submittedAs ||
        ticket?.accountType ||
        ticket?.type ||
        (ticket?.businessId || ticket?.business
          ? 'Business'
          : ticket?.customerId || ticket?.customer
            ? 'Customer'
            : 'Visitor');

  const overviewSubmittedBy =
    ticket?.submittedByName ||
    ticket?.customerName ||
    ticket?.businessName ||
    ticket?.customer?.name ||
    ticket?.business?.name ||
    '—';

  const showAccountIdField =
    overviewSubmittedAs === 'Customer' ||
    overviewSubmittedAs === 'Business' ||
    complianceTicket;

  const overviewAccountId = complianceTicket
    ? ticket?.businessId || ticket?.business?.id || null
    : overviewSubmittedAs === 'Customer'
      ? ticket?.customerId || ticket?.customer?.id || ticket?.accountId || null
      : overviewSubmittedAs === 'Business'
        ? ticket?.businessId || ticket?.business?.id || ticket?.accountId || null
        : null;

  const overviewAccountIdLabel =
    overviewSubmittedAs === 'Customer' ? 'Customer ID' : 'Business ID';

  const overviewTicketType = ticket?.ticketType || ticket?.category || '—';
  const overviewEntryPoint = formatEntryPointDisplay(ticket);
  const overviewSubmittedOn = ticket?.submittedOn || ticket?.createdDate;
  const relatedRecordPath = resolveRelatedRecordPath(ticket?.relatedRecordType, ticket?.relatedRecordId);

  const renderUpdatesAttachments = (attachments, isSupport = false) => {
    if (!attachments?.length) return null;
    const textClass = isSupport ? 'text-white' : 'text-offblack';
    const metaClass = isSupport ? 'text-white/80' : 'text-mediumgray';
    return (
      <div className="mt-2 space-y-1 min-w-0">
        {attachments.map((att) => (
          <button
            key={att.id || att.name}
            type="button"
            onClick={() => openAttachmentPreview(att)}
            title={att.name}
            className="flex items-center gap-2 w-full min-w-0 bg-transparent border-0 p-0 cursor-pointer text-left"
          >
            <i className={`fi fi-rs-file flex-shrink-0 text-sm ${textClass}`} aria-hidden />
            <span className="min-w-0 flex-1 overflow-hidden">
              <AdminMainText className={`${textClass} block truncate`}>
                {att.name}
              </AdminMainText>
              {(att.type || att.size) && (
                <AdminMetadataText className={`${metaClass} block truncate`}>
                  {att.type ? `${att.type}${att.size ? ` · ${att.size}` : ''}` : att.size}
                </AdminMetadataText>
              )}
            </span>
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-offblack">Loading ticket details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!ticket) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminAlertText className="mb-4">Ticket not found</AdminAlertText>
            <AdminHeaderAction  onClick={() => navigate('/admin/support/tickets')}>
              Back to Tickets
            </AdminHeaderAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'messages', label: 'Support Messages' },
    { id: 'resolution', label: 'Resolution' },
    { id: 'audit-log', label: 'Audit Log' },
  ];

  const formatTicketAuditWhen = (value) => {
    if (!value) return '—';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const ticketStatusNorm = normalizeTicketStatus(ticket.status);
  // Reply only while the ticket is still open — not Resolved / Closed / Withdrawn.
  const canReplyOnSupportMessages = !isTicketReplyClosed(ticket.status);
  const replySendDisabled = !replyText.trim() || Boolean(replyAttachmentError);
  const canMarkResolved =
    ticket.status === 'In Progress' || ticket.status === 'Awaiting Reply';
  const canReturnToInProgress = isWithinReopeningPeriod(ticket);
  const messageStatusActions = (() => {
    const status = ticketStatusNorm;
    const actions = [];
    if (status === 'New' || status === 'Awaiting Reply') {
      actions.push({
        key: 'mark-in-progress',
        label: 'Mark In Progress',
        onClick: () => handleChangeStatus('In Progress'),
      });
    }
    if (status === 'Resolved' && canReturnToInProgress) {
      actions.push({
        key: 'return-in-progress',
        label: 'Return to In Progress',
        onClick: () => handleChangeStatus('In Progress'),
      });
    }
    return actions;
  })();
  const hasActionsMenuItems =
    messageStatusActions.length > 0 ||
    (canReplyOnSupportMessages && (canMarkResolved || canCreateFsCase));
  const showResolvedActionsBar =
    !canReplyOnSupportMessages &&
    ticketStatusNorm === 'Resolved' &&
    hasActionsMenuItems;

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>

        {/* Screen 2 header — title only (metadata lives in Overview) */}
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <AdminPanelTitle className="text-offblack">{ticket.subject || '—'}</AdminPanelTitle>
        </DashboardMainContainer>

        {/* Tabs */}
        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div
            className={
              activeTab === 'messages'
                ? ADMIN_TAB_CONTENT_WIDE_CLASS
                : adminDetailTabContentClassName(activeTab)
            }
          >
            {/* TAB 1: OVERVIEW — ticket + linked-record details only (no message body, no status actions) */}
            {activeTab === 'overview' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Column 1 — identity */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Ticket ID</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{ticket.ticketId || '—'}</AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Submitted As</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{overviewSubmittedAs || '—'}</AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Submitted By</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{overviewSubmittedBy}</AdminDetailFieldValue>
                    </div>
                    {showAccountIdField ? (
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>{overviewAccountIdLabel}</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {overviewAccountId &&
                          (overviewSubmittedAs === 'Customer' && !complianceTicket) ? (
                            <AdminLinkAction
                              type="button"
                              onClick={() => navigate(`/admin/users/customers/${overviewAccountId}`)}
                              className="text-left"
                            >
                              {overviewAccountId}
                            </AdminLinkAction>
                          ) : overviewAccountId &&
                            (overviewSubmittedAs === 'Business' || complianceTicket) ? (
                            <AdminLinkAction
                              type="button"
                              onClick={() => navigate(`/admin/users/businesses/${overviewAccountId}`)}
                              className="text-left"
                            >
                              {overviewAccountId}
                            </AdminLinkAction>
                          ) : (
                            overviewAccountId || '—'
                          )}
                        </AdminDetailFieldValue>
                      </div>
                    ) : null}
                  </div>

                  {/* Column 2 — contact / classification */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Email</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {ticket.email || ticket.customer?.email || ticket.business?.email || '—'}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Entry Point</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{overviewEntryPoint}</AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Ticket Type</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{overviewTicketType}</AdminDetailFieldValue>
                    </div>
                    {complianceTicket ? (
                      <>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Response Period</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {ticket.complianceResponsePeriodDays != null
                              ? `${ticket.complianceResponsePeriodDays} calendar days`
                              : '—'}
                          </AdminDetailFieldValue>
                        </div>
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Due Date</AdminDetailFieldLabel>
                          <AdminDetailFieldValue>
                            {ticket.complianceDueDate
                              ? formatAuditDateTime(ticket.complianceDueDate)
                              : '—'}
                          </AdminDetailFieldValue>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* Column 3 — status / assignment */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{ticket.status || '—'}</AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Assigned Team Member</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {formatAssignedDisplay(ticket.assignee)}
                      </AdminDetailFieldValue>
                    </div>
                  </div>

                  {/* Column 4 — related record + dates */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Related Record Type</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>{ticket.relatedRecordType || '—'}</AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Related Record ID</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {ticket.relatedRecordId && relatedRecordPath ? (
                          <AdminLinkAction
                            type="button"
                            onClick={() => navigate(relatedRecordPath)}
                            className="text-left"
                          >
                            {ticket.relatedRecordId}
                          </AdminLinkAction>
                        ) : (
                          ticket.relatedRecordId || '—'
                        )}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Submitted On</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {overviewSubmittedOn ? formatAuditDateTime(overviewSubmittedOn) : '—'}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Last Updated</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {ticket.lastUpdated ? formatAuditDateTime(ticket.lastUpdated) : '—'}
                      </AdminDetailFieldValue>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: SUPPORT MESSAGES — conversation, notes, reply/note editor, handling actions */}
            {activeTab === 'messages' && (
              <div>
                <div
                  className="flex flex-col"
                  style={
                    canReplyOnSupportMessages
                      ? { height: 'calc(100vh - 512px)', minHeight: '400px' }
                      : {
                          height: 'calc(100vh - 360px)',
                          minHeight: '500px',
                          maxHeight: '800px',
                        }
                  }
                >
                  <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto space-y-4 p-6 pr-2 slim-scrollbar"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#C0C0C0 #F2F2F2',
                    }}
                  >
                    {supportMessagesThread.length === 0 ? (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">
                          No support messages yet.
                        </AdminSupportingText>
                      </div>
                    ) : (
                      supportMessagesThread.map((message) => {
                        const isSupport = Boolean(message.isSupport);
                        const messageTimestamp = message.timestamp
                          ? formatDateTime(message.timestamp)
                          : '—';
                        const avatarSrc = isSupport
                          ? null
                          : ticket.customer?.profilePicture ||
                            ticket.business?.logo ||
                            `https://i.pravatar.cc/100?img=1`;

                        return (
                          <div
                            key={message.id}
                            className={`flex ${isSupport ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`flex max-w-[49%] ${
                                isSupport ? 'flex-row-reverse' : 'flex-row'
                              } items-start gap-2`}
                            >
                              <div className="flex-shrink-0">
                                {isSupport ? (
                                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border border-lightgray ring-1 ring-offblack/5">
                                    <i className="fi fi-rs-headphones text-offblack text-sm" />
                                  </div>
                                ) : (
                                  <img
                                    src={avatarSrc}
                                    alt=""
                                    className="w-8 h-8 rounded-full object-cover"
                                  />
                                )}
                              </div>
                              <div className="flex flex-col min-w-0">
                                <AdminMetadataText
                                  className={`mb-1 px-1 ${
                                    isSupport ? 'text-right' : 'text-left'
                                  } text-mediumgray`}
                                >
                                  {message.senderName} — {message.senderType}
                                </AdminMetadataText>
                                <div
                                  className={`px-4 py-2 rounded-xl min-w-0 ${
                                    isSupport
                                      ? 'bg-primaryblue text-white rounded-tr-md'
                                      : 'bg-mistygray text-offblack rounded-tl-md'
                                  }`}
                                >
                                  <AdminMainText
                                    className={`${
                                      isSupport ? 'text-white' : 'text-offblack'
                                    } leading-relaxed whitespace-pre-wrap`}
                                  >
                                    {message.message}
                                  </AdminMainText>
                                  {renderUpdatesAttachments(message.attachments, isSupport)}
                                </div>
                                <div
                                  className={`mt-1 px-2 ${
                                    isSupport ? 'text-right' : 'text-left'
                                  }`}
                                >
                                  <AdminMetadataText className="text-mediumgray">
                                    {messageTimestamp}
                                  </AdminMetadataText>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {canReplyOnSupportMessages ? (
                  <div ref={replyComposerRef} className="p-5 border-t border-lightgray">
                    {actionError ? (
                      <AdminFormFieldError className="mb-3">{actionError}</AdminFormFieldError>
                    ) : null}

                    {showResolveForm ? (
                      <div className="space-y-3">
                        {complianceProtectionActive ? (
                          <AdminSupportingText className="text-mediumgray">
                            Waiting for the Compliance response. This ticket cannot be resolved yet.
                          </AdminSupportingText>
                        ) : null}
                        <div>
                          <AdminDetailFieldLabel className="mb-1.5 block">
                            Resolution Summary
                          </AdminDetailFieldLabel>
                          <textarea
                            value={resolutionSummary}
                            onChange={(e) => {
                              setResolutionSummary(e.target.value);
                              if (resolutionSummaryError && e.target.value.trim()) {
                                setResolutionSummaryError('');
                              }
                            }}
                            onBlur={() => {
                              if (!resolutionSummary.trim()) {
                                setResolutionSummaryError('Resolution Summary is required.');
                              }
                            }}
                            placeholder="Summarise the resolution outcome..."
                            className={adminFormTextareaClasses(Boolean(resolutionSummaryError))}
                            rows={4}
                            aria-invalid={Boolean(resolutionSummaryError)}
                            disabled={complianceProtectionActive}
                          />
                          {resolutionSummaryError ? (
                            <AdminAlertText className="mt-1.5">{resolutionSummaryError}</AdminAlertText>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <AdminFormSecondaryAction
                            type="button"
                            onClick={() => {
                              setShowResolveForm(false);
                              setResolutionSummaryError('');
                              setActionError('');
                            }}
                          >
                            Cancel
                          </AdminFormSecondaryAction>
                          <AdminHeaderAction
                            type="button"
                            onClick={handleMarkResolved}
                            disabled={complianceProtectionActive || !resolutionSummary.trim()}
                          >
                            Mark Resolved
                          </AdminHeaderAction>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <AdminDetailFieldLabel className="mb-1.5 block">Message</AdminDetailFieldLabel>
                          <textarea
                            value={replyText}
                            onChange={handleReplyTextChange}
                            onBlur={() => {
                              if (!replyText.trim()) setReplyError('Message is required.');
                            }}
                            placeholder="Type your reply here..."
                            className={adminFormTextareaClasses(Boolean(replyError))}
                            rows={5}
                            aria-invalid={Boolean(replyError)}
                          />
                          {replyError ? (
                            <AdminAlertText className="mt-1.5">{replyError}</AdminAlertText>
                          ) : null}
                        </div>
                        <input
                          ref={replyFileInputRef}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.txt"
                          onChange={handleReplyFileChange}
                          className="hidden"
                        />
                        <div className="flex flex-wrap items-center gap-3">
                          {hasActionsMenuItems ? (
                            <div className="relative" ref={composerMenuRef}>
                              <AdminRowActionsButton
                                className="!w-10 !h-10 border border-mediumgray bg-white hover:bg-pastalBlueWhite hover:border-primaryblue"
                                onClick={() => setComposerMenuOpen((open) => !open)}
                                title="Actions"
                                aria-label="Actions"
                                aria-expanded={composerMenuOpen}
                                aria-haspopup="menu"
                              >
                                <AdminRowMenuIcon />
                              </AdminRowActionsButton>
                              {composerMenuOpen && (
                                <div
                                  role="menu"
                                  className="absolute left-0 bottom-full mb-1 z-30 min-w-[200px] w-max max-w-[16rem] rounded-xl border border-lightgray bg-white py-2 shadow-lg"
                                >
                                  {messageStatusActions.map((action) => (
                                    <AdminRowMenuItem
                                      key={action.key}
                                      onClick={() => {
                                        setComposerMenuOpen(false);
                                        action.onClick();
                                      }}
                                    >
                                      {action.label}
                                    </AdminRowMenuItem>
                                  ))}
                                  {canMarkResolved ? (
                                    <AdminRowMenuItem
                                      disabled={complianceProtectionActive}
                                      onClick={() => {
                                        setComposerMenuOpen(false);
                                        setShowResolveForm(true);
                                        setActionError('');
                                      }}
                                    >
                                      Mark Resolved
                                    </AdminRowMenuItem>
                                  ) : null}
                                  {canCreateFsCase ? (
                                    <AdminRowMenuItem
                                      onClick={() => {
                                        setComposerMenuOpen(false);
                                        openCreateFsModal();
                                      }}
                                    >
                                      Create F&S Case
                                    </AdminRowMenuItem>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ) : null}
                          <AdminRowActionsButton
                            className="!w-10 !h-10 border border-mediumgray bg-white hover:bg-pastalBlueWhite hover:border-primaryblue"
                            onClick={() => replyFileInputRef.current?.click()}
                            title="Add Attachment"
                            aria-label="Add Attachment"
                          >
                            <i className="fi fi-rs-clip text-base flex items-center justify-center w-full h-full" />
                          </AdminRowActionsButton>
                          <AdminHeaderAction
                            type="button"
                            onClick={handleSendReply}
                            disabled={replySendDisabled}
                          >
                            Send Reply
                          </AdminHeaderAction>
                          {replyFile && (
                            <AdminMetadataText className="text-mediumgray flex items-center gap-2">
                              <i className="fi fi-rs-check-circle text-freshgreen" aria-hidden />
                              {replyFile.name}
                              <AdminLinkAction
                                type="button"
                                onClick={() => {
                                  setReplyFile(null);
                                  setReplyAttachmentError('');
                                  if (replyFileInputRef.current) replyFileInputRef.current.value = '';
                                }}
                              >
                                Remove
                              </AdminLinkAction>
                            </AdminMetadataText>
                          )}
                        </div>
                        {replyAttachmentError ? (
                          <AdminAlertText className="mt-1.5">{replyAttachmentError}</AdminAlertText>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : showResolvedActionsBar ? (
                  <div className="px-5 py-4 border-t border-lightgray flex flex-wrap items-center gap-3">
                    <AdminSupportingText className="text-mediumgray flex-1 min-w-0">
                      This ticket is Resolved. Support Messages are read-only.
                    </AdminSupportingText>
                    <div className="relative" ref={composerMenuRef}>
                      <AdminRowActionsButton
                        className="!w-10 !h-10 border border-mediumgray bg-white hover:bg-pastalBlueWhite hover:border-primaryblue"
                        onClick={() => setComposerMenuOpen((open) => !open)}
                        title="Actions"
                        aria-label="Actions"
                        aria-expanded={composerMenuOpen}
                        aria-haspopup="menu"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {composerMenuOpen && (
                        <div
                          role="menu"
                          className="absolute right-0 bottom-full mb-1 z-30 min-w-[200px] w-max max-w-[16rem] rounded-xl border border-lightgray bg-white py-2 shadow-lg"
                        >
                          {messageStatusActions.map((action) => (
                            <AdminRowMenuItem
                              key={action.key}
                              onClick={() => {
                                setComposerMenuOpen(false);
                                action.onClick();
                              }}
                            >
                              {action.label}
                            </AdminRowMenuItem>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="px-5 pb-4">
                    <AdminSupportingText className="text-mediumgray">
                      This ticket is {ticketStatusNorm}. Support Messages are read-only.
                    </AdminSupportingText>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: RESOLUTION — saved Resolution records only */}
            {activeTab === 'resolution' && (
              <div>
                {(ticket.resolutions || []).length > 0 ? (
                  <div className="space-y-8">
                    {(ticket.resolutions || []).map((record) => (
                      <div
                        key={record.resolutionRecordId}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                      >
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Resolution Record ID</AdminDetailFieldLabel>
                            <AdminDetailFieldValue>
                              {record.resolutionRecordId || '—'}
                            </AdminDetailFieldValue>
                          </div>
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Resolution Summary</AdminDetailFieldLabel>
                            <AdminDetailFieldValue className="whitespace-pre-wrap">
                              {record.resolutionSummary || '—'}
                            </AdminDetailFieldValue>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Resolved By</AdminDetailFieldLabel>
                            <AdminDetailFieldValue>{record.resolvedBy || '—'}</AdminDetailFieldValue>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Resolved On</AdminDetailFieldLabel>
                            <AdminDetailFieldValue>
                              {record.resolvedOn ? formatAuditDateTime(record.resolvedOn) : '—'}
                            </AdminDetailFieldValue>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Reopening Until</AdminDetailFieldLabel>
                            <AdminDetailFieldValue>
                              {record.reopeningUntil
                                ? formatAuditDateTime(record.reopeningUntil)
                                : '—'}
                            </AdminDetailFieldValue>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <AdminSupportingText className="text-mediumgray">
                    No resolution recorded.
                  </AdminSupportingText>
                )}
              </div>
            )}

            {activeTab === 'audit-log' && (
              <div className="min-w-0 max-w-full">
                <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                  <div className="w-full min-w-0 max-w-full">
                    <div
                      className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                      style={{
                        gridTemplateColumns:
                          'minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.6fr) minmax(0, 0.9fr)',
                      }}
                    >
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Date and Time
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Event
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Details
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Related Record
                      </AdminDetailTableHeader>
                    </div>
                    {(ticket.auditLogs || []).length > 0 ? (
                      (ticket.auditLogs || []).map((log, index) => (
                        <div
                          key={log.changeId || `ticket-audit-${index}`}
                          className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                          style={{
                            gridTemplateColumns:
                              'minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.6fr) minmax(0, 0.9fr)',
                          }}
                        >
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {formatTicketAuditWhen(log.dateTime)}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {log.event || '—'}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {log.details || '—'}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {log.relatedRecord || '—'}
                          </AdminTableBody>
                        </div>
                      ))
                    ) : (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">
                          No audit log entries
                        </AdminSupportingText>
                      </div>
                    )}
                  </div>
                </DashboardMainContainer>
              </div>
            )}

          </div>
        </DashboardMainContainer>
        </div>
      </div>

      {attachmentPreview && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4 py-8"
          onClick={closeAttachmentPreview}
          role="presentation"
        >
          <div
            className="w-full max-w-[min(100vw-32px,896px)] max-h-[90vh] flex flex-col rounded-xl bg-white border border-lightgray shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={attachmentPreview.attachment?.name || 'Attachment preview'}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-lightgray shrink-0">
              <AdminPanelTitle className="text-left flex-1 min-w-0 !mb-0 break-words normal-case">{attachmentPreview.attachment?.name || 'Attachment'}</AdminPanelTitle>
              <button
                type="button"
                onClick={closeAttachmentPreview}
                className="flex-shrink-0 text-base text-offblack hover:text-primaryblue p-1 rounded-lg hover:bg-pastalBlueWhite transition-colors"
                aria-label="Close preview"
              >
                <i className="fi fi-rs-cross flex items-center justify-center w-full h-full" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-5 bg-pastalBlueWhite">
              {attachmentPreview.kind === 'image' && attachmentPreview.url && (
                <div className="flex justify-center rounded-lg bg-white border border-lightgray overflow-hidden">
                  <img
                    src={attachmentPreview.url}
                    alt=""
                    className="max-h-[75vh] w-auto max-w-full object-contain"
                  />
                </div>
              )}
              {attachmentPreview.kind === 'pdf' && attachmentPreview.url && (
                <iframe
                  title={attachmentPreview.attachment?.name || 'PDF preview'}
                  src={attachmentPreview.url}
                  className="w-full min-h-[70vh] rounded-lg border border-lightgray bg-white"
                />
              )}
              {(attachmentPreview.kind === 'other' || !attachmentPreview.url) && (
                <div className="rounded-xl bg-white border border-lightgray p-6 text-center">
                  <AdminSupportingText className="mb-4">
                    {attachmentPreview.url
                      ? 'This file opens in a new tab. Use the button below when you’re ready.'
                      : 'No preview is available for this attachment in the sandbox. When the ticket is wired to storage, the file will open here or download.'}
                  </AdminSupportingText>
                  {attachmentPreview.url && (
                    <AdminRowAction onClick={() => window.open(attachmentPreview.url, '_blank', 'noopener,noreferrer')}>Open in new tab</AdminRowAction>
                  )}
                </div>
              )}
              {attachmentPreview.url &&
                (attachmentPreview.kind === 'image' || attachmentPreview.kind === 'pdf') && (
                  <div className="flex flex-wrap justify-center gap-3 mt-6">
                    <AdminRowAction onClick={() => window.open(attachmentPreview.url, '_blank', 'noopener,noreferrer')}>Open in new tab</AdminRowAction>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {createFsModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeCreateFsModal}
        >
          <div
            className={`bg-white rounded-xl p-6 ${POPUP_FORM_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">Create F&S Case</AdminPanelTitle>
              <button
                type="button"
                onClick={closeCreateFsModal}
                className="text-offblack hover:text-primaryblue shrink-0"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross dashboard-icon" />
              </button>
            </div>
            {fsFormError ? (
              <AdminFormFieldError className="mb-3">{fsFormError}</AdminFormFieldError>
            ) : null}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Source</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>Support Tickets</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Ticket ID</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{ticket?.ticketId || '—'}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Ticket Type</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>
                    {ticket?.ticketType || ticket?.category || '—'}
                  </AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Reporter</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>
                    {ticket
                      ? `${getTicketSubmitterName(ticket)}${
                          getTicketSubmitterType(ticket) !== '—'
                            ? ` (${getTicketSubmitterType(ticket)})`
                            : ''
                        }`
                      : '—'}
                  </AdminDetailFieldValue>
                </div>
              </div>
              <div>
                <AdminDetailFieldLabel className="mb-1.5">Affected account</AdminDetailFieldLabel>
                <input
                  type="text"
                  value={fsAffectedAccount}
                  onChange={(e) => {
                    setFsAffectedAccount(e.target.value);
                    if (fsFormError) setFsFormError('');
                  }}
                  placeholder="Customer or Business ID"
                  className={adminFormInputClasses(false, false)}
                />
              </div>
              <div>
                <AdminDetailFieldLabel className="mb-1.5">Concern summary</AdminDetailFieldLabel>
                <textarea
                  value={fsConcernSummary}
                  onChange={(e) => {
                    setFsConcernSummary(e.target.value);
                    if (fsFormError) setFsFormError('');
                  }}
                  placeholder="Explain the issue requiring investigation..."
                  className={adminFormTextareaClasses(false)}
                  rows={4}
                />
              </div>
              <div>
                <AdminDetailFieldLabel className="mb-1.5">Relevant evidence</AdminDetailFieldLabel>
                <textarea
                  value={fsEvidence}
                  onChange={(e) => setFsEvidence(e.target.value)}
                  placeholder="Reference relevant ticket messages and attachments..."
                  className={adminFormTextareaClasses(false)}
                  rows={3}
                />
              </div>
              <div>
                <AdminDetailFieldLabel className="mb-1.5">F&S Priority</AdminDetailFieldLabel>
                <div className="relative w-full min-w-0">
                  <select
                    value={fsPriority}
                    onChange={(e) => setFsPriority(e.target.value)}
                    className={adminWizardSelectClasses()}
                  >
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                  <span
                    className="pointer-events-none absolute right-2.5 top-1/2 z-[5] flex h-3.5 w-3.5 shrink-0 -translate-y-1/2 items-center justify-center"
                    aria-hidden
                  >
                    <i className="fi fi-rs-angle-down block !text-[14px] !leading-none text-offblack" />
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={closeCreateFsModal}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={handleSaveCreateFsCase}>
                Save
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default TicketDetail;
